import { useState } from 'react';
import { ListenerRole } from '../../hooks/usePeers';
import { ScheduleHistoryParams, useScheduleHistoryDownload } from '../../hooks/useScheduleReporting';
import PeerListenerSelectorModal from './peer-listener-select-modal';
import { Toggle } from '../ui';
import { TimezoneDisplay } from '../../containers/peer-schedule/schedule-history/history-constants';
import { CrisisState } from '../../hooks/useChatBot';

interface ScheduleFiltersProps {
  setScheduleHistoryFilters: (filters: ScheduleHistoryParams) => void;
  scheduleHistoryFilters: ScheduleHistoryParams;
  timezoneDisplay?: string;
  setTimezoneDisplay?: (timezone: string) => void;
  listeners: ListenerRole[] | undefined;
  listenerOrPeer?: 'Listener' | 'Peer';
  downloadCSV?: boolean;
  setPeerCrisisState?: (state: CrisisState | undefined) => void;
  // Only sent from Overview page, not History page.
  domains?: { id: number; name: string }[];
  domainId?: number;
  setDomainId?: (id: number | undefined) => void;
}

const ScheduleFilters: React.FC<ScheduleFiltersProps> = ({
  setScheduleHistoryFilters,
  scheduleHistoryFilters,
  timezoneDisplay,
  setTimezoneDisplay,
  listeners,
  listenerOrPeer = 'Listener',
  downloadCSV = true,
  setPeerCrisisState,
  domains,
  domainId,
  setDomainId,
}) => {
  const { isFetching, refetch } = useScheduleHistoryDownload(scheduleHistoryFilters, false);
  const [showPeerModal, setShowPeerModal] = useState(false);

  const onDownloadCSV = () => {
    if (
      scheduleHistoryFilters.listener_role_ids.length > 0 &&
      scheduleHistoryFilters.start_date &&
      scheduleHistoryFilters.end_date
    ) {
      refetch();
    }
  };

  const getSelectedListenerNames = () => {
    if (scheduleHistoryFilters.listener_role_ids.length === 0) return `Select ${listenerOrPeer}`;
    if (scheduleHistoryFilters.listener_role_ids.length === 1) {
      const listener = listeners?.find((l) => l.id === scheduleHistoryFilters.listener_role_ids[0]);
      return listener?.user.display_name || `Select ${listenerOrPeer}s`;
    }
    return `${scheduleHistoryFilters.listener_role_ids.length} ${listenerOrPeer}s Selected`;
  };

  return (
    <div className="gray-border-line bg-gray-background-light flex h-12 w-full items-center space-x-2 border-b px-4">
      <div className="flex w-full items-center justify-between gap-6">
        <div className="flex gap-6">
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-2">
              <label className="font-medium text-neutral-600">Date Range</label>
              <input
                type="date"
                className="w-48 rounded border bg-white p-2"
                value={scheduleHistoryFilters.start_date}
                onChange={(e) => setScheduleHistoryFilters({ ...scheduleHistoryFilters, start_date: e.target.value })}
              />
              <span className="text-neutral-600">to</span>
              <input
                type="date"
                className="w-48 rounded border bg-white p-2"
                value={scheduleHistoryFilters.end_date}
                onChange={(e) => setScheduleHistoryFilters({ ...scheduleHistoryFilters, end_date: e.target.value })}
              />
            </div>
          </div>
          {domains && setDomainId && (
            <div className="flex items-center gap-2">
              <label className="font-medium text-neutral-600">Domain</label>
              <select
                value={domainId ?? ''}
                onChange={(e) => {
                  const next = e.target.value ? parseInt(e.target.value, 10) : undefined;
                  setDomainId(next);
                  setScheduleHistoryFilters({ ...scheduleHistoryFilters, listener_role_ids: [] });
                }}
                className="w-56 rounded border bg-white p-2"
              >
                <option value="">All domains</option>
                {domains.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div className="flex items-center gap-2">
            <label className="font-medium text-neutral-600">Peer</label>
            <button onClick={() => setShowPeerModal(true)} className="w-48 rounded border bg-white p-2 text-left">
              {getSelectedListenerNames()}
            </button>
          </div>
          <div className="flex items-center gap-2">
            {setPeerCrisisState && (
              <>
                <label>Crisis State:</label>
                <select
                  defaultValue=""
                  className="w-full"
                  onChange={(e) => {
                    e.target.value === ''
                      ? setPeerCrisisState(undefined)
                      : setPeerCrisisState(e.target.value as CrisisState);
                  }}
                >
                  <option></option>
                  {Object.values(CrisisState).map((state) => {
                    return <option>{state}</option>;
                  })}
                </select>
              </>
            )}
          </div>
        </div>
        {timezoneDisplay && setTimezoneDisplay && (
          <div className="flex items-center gap-x-2 text-sm">
            <div className="whitespace-nowrap">Central Time</div>
            <Toggle
              checked={timezoneDisplay === TimezoneDisplay.Listener}
              onChange={() => {
                setTimezoneDisplay(
                  timezoneDisplay === TimezoneDisplay.Listener ? TimezoneDisplay.Central : TimezoneDisplay.Listener,
                );
              }}
            />
            <div className="whitespace-nowrap">Listener Time</div>
          </div>
        )}
        {downloadCSV && (
          <button
            onClick={onDownloadCSV}
            className="rounded bg-violet-950 px-4 py-2 text-white transition-colors hover:bg-violet-900"
            disabled={
              scheduleHistoryFilters.listener_role_ids.length === 0 ||
              !scheduleHistoryFilters.start_date ||
              !scheduleHistoryFilters.end_date ||
              isFetching
            }
          >
            {isFetching ? 'Downloading...' : 'Download CSV'}
          </button>
        )}
      </div>
      <PeerListenerSelectorModal
        showModal={showPeerModal}
        setShowModal={setShowPeerModal}
        selectedListenerIds={scheduleHistoryFilters.listener_role_ids}
        setSelectedListenerIds={(ids) =>
          setScheduleHistoryFilters({ ...scheduleHistoryFilters, listener_role_ids: ids })
        }
        listeners={listeners}
        listenerOrPeer={listenerOrPeer}
      />
    </div>
  );
};

export default ScheduleFilters;
